/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

import useStore from '@store/index';
import {
	TParams,
	TQueryParams,
	TSelectOption,
} from '../../../types';
import { notifications } from '@mantine/notifications';
import {
	cloneDeep,
	removeEmptyArrays,
} from '@utils/functions';
import {
	TAdditionalProject,
	TAdditionalProjectQuaryParams,
} from '../types';
import Decimal from 'decimal.js';
import { useTranslation } from 'react-i18next';

/* -------------------------------------------------------------------------- */
/*                                  Hook                                      */
/* -------------------------------------------------------------------------- */
export const useOffsetProjectAdditional = () => {
	const {
		setOffsetAdditionalProjectsPage,
		setOffsetAdditionalProjectsQueryParams,
		offsetAdditionalProjectsQueryParams,
		offsetAdditionalProjectsSelected,
		setOffsetAdditionalProjectsSelected,
		setOffsetProjectMixinAddi,
		setOffsetCreationActiveStep,
		offsetCreationActiveStep,
		offsetProducedAddis,
		setOffsetAdditionalProjects,
		offsetAdditionalProjects,
	} = useStore();

	const { t } = useTranslation();

	/*************************************** Filters  ***************************************/
	const handleActiveFilter = (status: TQueryParams[]) => {
		setOffsetAdditionalProjectsPage(1);
		const queryParamsActive = {
			active: status.map(
				(_status: TParams) => _status.value
			),
		};

		setOffsetAdditionalProjectsQueryParams({
			...offsetAdditionalProjectsQueryParams,
			...queryParamsActive,
		});
	};

	/**
	 * Filter list by project
	 *
	 * @param {TSelectOption} _projects
	 */
	const handleProjectFilter = (
		projects: TSelectOption[]
	) => {
		setOffsetAdditionalProjectsPage(1);
		const queryParamsProjects = {
			_id: projects.map(
				(_project: TSelectOption) => _project.value
			),
		};

		const filterParams: TAdditionalProjectQuaryParams =
			removeEmptyArrays({
				...offsetAdditionalProjectsQueryParams,
				...queryParamsProjects,
			});
		setOffsetAdditionalProjectsQueryParams(filterParams);
	};

	/**
	 * Filter list by country
	 *
	 * @param {TSelectOption} _countries
	 */
	const handleCountryFilter = (
		_countries: TSelectOption[]
	) => {
		setOffsetAdditionalProjectsPage(1);

		const countries = _countries.map(
			(_country: TSelectOption) => _country.value
		);

		if (countries.length == 0) {
			const _offsetMesurabilityProjectsQueryParams =
				cloneDeep(offsetAdditionalProjectsQueryParams);
			delete _offsetMesurabilityProjectsQueryParams[
				'location.countryCode'
			];
			setOffsetAdditionalProjectsQueryParams(
				_offsetMesurabilityProjectsQueryParams
			);
			return;
		}
		const queryParamsCountries = {
			'location.countryCode': {
				$in: countries,
			},
		};
		const filterParams: TAdditionalProjectQuaryParams =
			removeEmptyArrays({
				...offsetAdditionalProjectsQueryParams,
				...queryParamsCountries,
			});
		setOffsetAdditionalProjectsQueryParams(filterParams);
	};

	/**
	 * Filter list by technology
	 *
	 * @param {TSelectOption} _technologies
	 */
	const handleTechnologyFilter = (
		projects: TSelectOption[]
	) => {
		setOffsetAdditionalProjectsPage(1);
		const queryParamsProjects = {
			technology: projects.map(
				(_project: TSelectOption) => _project.value
			),
		};
		setOffsetAdditionalProjectsQueryParams({
			...offsetAdditionalProjectsQueryParams,
			...queryParamsProjects,
		});
	};

	const isSelectedProjectAdditional = (
		id: string,
		offsetAdditionalProjects: TAdditionalProject[]
	) => {
		return offsetAdditionalProjects.some(
			(item: TAdditionalProject) => item.id === id
		);
	};

	const handleRemoveSelectedProject = (
		item: TAdditionalProject
	) => {
		setOffsetAdditionalProjectsSelected([
			...offsetAdditionalProjectsSelected.filter(
				(_projectSelected) =>
					_projectSelected.id !== item.id
			),
		]);
	};

	const handleNextStep = () => {
		const _parsedOffsetAdditionalProjectsSelected =
			offsetAdditionalProjectsSelected.map(
				(item: TAdditionalProject) => ({
					[item.id]: item.price,
				})
			);

		const finalObject =
			_parsedOffsetAdditionalProjectsSelected.reduce(
				(acc, item) => {
					const key = Object.keys(item)[0];
					const value = Object.values(item)[0];
					acc[key] = value;
					return acc;
				},
				{}
			);

		const bodyProducedAddis = {
			projectMixin_addi: finalObject,
		};
		setOffsetProjectMixinAddi(bodyProducedAddis);

		setOffsetCreationActiveStep(
			offsetCreationActiveStep + 1
		);
	};

	const handleAddProject = (
		project: TAdditionalProject
	) => {
		const MAX_PROJECTS = 4;

		if (
			Array.isArray(offsetAdditionalProjectsSelected) &&
			offsetAdditionalProjectsSelected.length >=
				MAX_PROJECTS
		) {
			notifications.show({
				title: t('global.error'),
				message: t(
					'offsets.notifications.youCantSelectMoreThan4Projects'
				),
				color: 'error',
			});
		} else {
			const financialNeeds = parseFloat(
				project.financialNeeds
			);
			const coverage = parseFloat(project.coverage);

			const financialNeedsCoverage =
				financialNeeds - coverage;

			const idExiste =
				offsetAdditionalProjectsSelected.some(
					(_project: TAdditionalProject) =>
						_project.id === project.id
				);

			if (idExiste) {
				notifications.show({
					title: t('global.error'),
					message: t(
						'offsets.notifications.thisProjectHasAlreadyBeenSelected'
					),
					color: 'error',
				});
			} else {
				if (
					offsetAdditionalProjectsSelected &&
					Array.isArray(
						offsetAdditionalProjectsSelected
					) &&
					offsetAdditionalProjectsSelected.length == 0
				) {
					if (project.financialNeeds !== 'Unlimited') {
						if (
							offsetProducedAddis > financialNeedsCoverage
						) {
							setOffsetAdditionalProjectsSelected([
								...offsetAdditionalProjectsSelected,
								{
									...project,
									price: financialNeedsCoverage,
								},
							]);
						} else {
							setOffsetAdditionalProjectsSelected([
								...offsetAdditionalProjectsSelected,
								{
									...project,
									price: Number(offsetProducedAddis),
								},
							]);
						}
					} else {
						setOffsetAdditionalProjectsSelected([
							...offsetAdditionalProjectsSelected,
							{
								...project,
								price: Number(offsetProducedAddis),
							},
						]);
					}
				} else {
					const _cloneOffsetAdditionalProjectsSelected =
						cloneDeep(offsetAdditionalProjectsSelected);

					const totalSelectedProjectsPrices =
						_cloneOffsetAdditionalProjectsSelected.reduce(
							(
								_acc: number,
								_project: TAdditionalProject
							) => _acc + _project.price,
							0
						);

					const _offsetProducedAddis = new Decimal(
						offsetProducedAddis
					);

					const _totalSelectedProjectsPrices =
						new Decimal(totalSelectedProjectsPrices);

					let remainingPrices = _offsetProducedAddis
						.sub(_totalSelectedProjectsPrices)
						.toNumber();

					// If the remaining price is negative, set it to zero.
					if (remainingPrices < 0) {
						remainingPrices = 0;
					}

					if (project.financialNeeds == 'Unlimited') {
						setOffsetAdditionalProjectsSelected([
							...offsetAdditionalProjectsSelected,
							{
								...project,
								price: Number(remainingPrices.toFixed(2)),
							},
						]);
						return;
					}

					// Check coverage to add project on additional
					if (remainingPrices > financialNeedsCoverage) {
						setOffsetAdditionalProjectsSelected([
							...offsetAdditionalProjectsSelected,
							{
								...project,
								price: financialNeedsCoverage,
							},
						]);
					} else {
						setOffsetAdditionalProjectsSelected([
							...offsetAdditionalProjectsSelected,
							{
								...project,
								price: Number(remainingPrices.toFixed(2)),
							},
						]);
					}
				}
			}
		}
	};

	const handleOnChangePrice = (
		project: TAdditionalProject,
		_price: number
	) => {
		const _cloneOffsetAdditionalProjects = JSON.parse(
			JSON.stringify(offsetAdditionalProjects, null, 2)
		);
		const _cloneOffsetAdditionalProjectsSelected =
			JSON.parse(
				JSON.stringify(
					offsetAdditionalProjectsSelected,
					null,
					2
				)
			);

		let newOffsetAdditionalProjects = [];
		let newOffsetAdditionalProjectsSelected = [];
		const objectToUpdate =
			_cloneOffsetAdditionalProjects.find(
				(obj: TAdditionalProject) => obj.id === project.id
			);

		// Update global list
		if (objectToUpdate) {
			newOffsetAdditionalProjects =
				_cloneOffsetAdditionalProjects.map(
					(project: TAdditionalProject) =>
						project.id === objectToUpdate.id
							? {
									...project,
									price: _price,
									// eslint-disable-next-line no-mixed-spaces-and-tabs
								}
							: project
				);
			setOffsetAdditionalProjects(
				newOffsetAdditionalProjects
			);

			newOffsetAdditionalProjectsSelected =
				_cloneOffsetAdditionalProjectsSelected.map(
					(project: TAdditionalProject) =>
						project.id === objectToUpdate.id
							? {
									...project,
									price: _price,
									// eslint-disable-next-line no-mixed-spaces-and-tabs
								}
							: project
				);
			setOffsetAdditionalProjectsSelected(
				newOffsetAdditionalProjectsSelected
			);
		} else {
			newOffsetAdditionalProjectsSelected =
				_cloneOffsetAdditionalProjectsSelected.map(
					(_project: TAdditionalProject) =>
						_project.id === project.id
							? {
									...project,
									price: _price,
									// eslint-disable-next-line no-mixed-spaces-and-tabs
								}
							: project
				);
			setOffsetAdditionalProjectsSelected(
				newOffsetAdditionalProjectsSelected
			);
		}
	};

	return {
		handleProjectFilter,
		handleCountryFilter,
		handleTechnologyFilter,
		isSelectedProjectAdditional,
		handleRemoveSelectedProject,
		handleNextStep,
		handleAddProject,
		handleOnChangePrice,
		handleActiveFilter,
	};
};
